import { RadioGroup } from '@mui/material'
import { ReactComponent as CheckIcon } from 'assets/icons/ellipse-checked.svg'
import { ReactComponent as MinusIcon } from 'assets/icons/outline_minus_button.svg'
import { ReactComponent as PlusIcon } from 'assets/icons/outline_plus_button.svg'
import { Button } from 'components'
import CustomDatePicker from 'components/CustomDatePicker/CustomDatePicker'
import CustomTimePicker from 'components/CustomTimePicker/CustomTimePicker'
import moment from 'moment'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'
import { convertTimePeriodToMoment, convertUTCToPartnerTimezone } from 'utils'
import { v4 as uuidv4 } from 'uuid'

import {
  AvailabilityList,
  CustomTimeSectionWrapper,
  FormElement,
  HolidayHoursAvailabilityRow,
  HolidayHoursAvailableDay,
  HolidayHoursAvailableTimeContainer,
  IconButton,
  MenuAvailabilityDialogActions,
  MenuAvailabilityErrorText,
  SectionLabel,
  SectionSeparator,
  StyledRadio,
  TimePickersWrapper,
} from '../styles'
import { HolidayHour, HolidayHourWithTimePeriods } from './HolidayHours'

// Enum to define operating modes
enum OperatingMode {
  CLOSED = 'closed',
  CUSTOM = 'custom',
}

const isEndTimeAfterStartTime = (startTime: string, endTime: string): boolean => {
  if (!startTime || !endTime) return false
  return moment(startTime, 'HH:mm').isAfter(moment(endTime, 'HH:mm'))
}

type Props = {
  holidayHours: HolidayHourWithTimePeriods[]
  partnerTimezone: string
  saveButtonDisabled: boolean
  onSave: (rows: HolidayHour[]) => void
  onCancel: () => void
}

const HolidayHoursForm = ({ holidayHours, partnerTimezone, saveButtonDisabled, onSave, onCancel }: Props) => {
  const [hours, setHours] = useState<HolidayHour[]>([])
  const [hasErrors, setHasErrors] = useState(false)
  const timePickerRefs = useRef<(HTMLInputElement | null)[]>([])

  // Initialize refs array with the correct size
  useEffect(() => {
    const totalTimePickers = hours.reduce((acc, row) => {
      return acc + (row.closed ? 0 : 2) // Each non-closed row has 2 time pickers
    }, 0)
    timePickerRefs.current = Array(totalTimePickers).fill(null)
  }, [hours])

  const handleTimeSelected = (rowIndex: number) => {
    // Calculate the correct index for the next time picker
    const currentIndex = rowIndex * 2
    const nextTimePickerIndex = currentIndex + 1

    // Use setTimeout to ensure the focus happens after the current event cycle
    setTimeout(() => {
      // Only focus if the next time picker exists
      if (timePickerRefs.current[nextTimePickerIndex]) {
        timePickerRefs.current[nextTimePickerIndex]?.focus()
      }
    }, 0)
  }

  useEffect(() => {
    const transformed = holidayHours.map((hour) => {
      const dateInTZ = convertUTCToPartnerTimezone(hour.attributes.date, partnerTimezone)
      const isoDate = moment(dateInTZ).format('YYYY-MM-DD')

      return {
        id: hour.id,
        date: isoDate,
        closed: hour.attributes.closed,
        timePeriod: hour.attributes.closed
          ? undefined
          : {
              startTime: hour.time_period?.start_time || '',
              endTime: hour.time_period?.end_time || '',
            },
      }
    })
    setHours(transformed)
  }, [holidayHours, partnerTimezone])

  const isDuplicateDate = (date: string, rowId: string): boolean => {
    return hours.some((row) => row.id !== rowId && row.date === date)
  }

  const updateHour = (rowId: string, update: Partial<HolidayHour>, shouldValidate = true) => {
    const updated = hours.map((row) => (row.id === rowId ? { ...row, ...update } : row))
    shouldValidate && validate(updated)
    setHours(updated)
  }

  const addHour = () => {
    setHours([
      ...hours,
      {
        id: uuidv4(),
        date: '',
        closed: true,
      },
    ])
  }

  const removeHour = (rowId: string) => {
    setHours((prevHours) => {
      const updatedHours = prevHours.filter((row) => row.id !== rowId)
      validate(updatedHours)
      return updatedHours
    })
  }

  const validate = (data: HolidayHour[]) => {
    const error = data.some((row) => {
      if (row.closed) return false
      const { startTime, endTime } = row.timePeriod || {}
      if (!startTime || !endTime) return true
      return isEndTimeAfterStartTime(startTime, endTime)
    })
    setHasErrors(error)
  }

  const handleSave = () => {
    if (hasErrors) return
    onSave(hours)
  }

  return (
    <>
      <AvailabilityList>
        {hours.map((row, rowIndex) => {
          const { startTime, endTime } = row.timePeriod || {}

          return (
            <HolidayHoursAvailabilityRow key={row.id}>
              <HolidayHoursAvailableDay>
                <CustomDatePicker
                  value={row.date ? moment(row.date, 'YYYY-MM-DD').toDate() : null}
                  onChange={(date: Date | null) => {
                    if (!date) return
                    const isoDate = moment(date).format('YYYY-MM-DD')
                    if (isDuplicateDate(isoDate, row.id)) {
                      toast.error('Date already exists. Please select another.', {
                        autoClose: 5000,
                      })
                    } else {
                      updateHour(row.id, { date: isoDate })
                    }
                  }}
                  shouldDisableDate={(date) => {
                    // Disable only if the date is strictly before today (ignores time)
                    return moment(date).isBefore(moment().startOf('day'), 'day')
                  }}
                  required
                  error={!row.date}
                />
                <IconButton onClick={() => removeHour(row.id)} title='Remove holiday'>
                  <MinusIcon />
                </IconButton>
              </HolidayHoursAvailableDay>

              <HolidayHoursAvailableTimeContainer>
                <SectionLabel>Operating Hours</SectionLabel>
                <RadioGroup
                  value={row.closed ? OperatingMode.CLOSED : OperatingMode.CUSTOM}
                  onChange={(e) =>
                    updateHour(row.id, { closed: e.target.value === OperatingMode.CLOSED }, false)
                  }
                >
                  <FormElement
                    value={OperatingMode.CLOSED}
                    control={<StyledRadio checkedIcon={<CheckIcon />} />}
                    label='Closed all day'
                  />
                  <CustomTimeSectionWrapper>
                    <FormElement
                      value={OperatingMode.CUSTOM}
                      control={<StyledRadio checkedIcon={<CheckIcon />} />}
                      label='Open during a custom time'
                    />
                    {row.closed === false && (
                      <>
                        <TimePickersWrapper data-testid='time-pickers-wrapper'>
                          <CustomTimePicker
                            key={`${row.id}-opens`}
                            label='Opens at'
                            defaultValue={convertTimePeriodToMoment(startTime)?.format('h:mm A')}
                            onChange={(val) => {
                              const endTime = row.timePeriod?.endTime || ''
                              const formatted = val ? moment(val, 'h:mm A').format('HH:mm') : ''
                              updateHour(row.id, {
                                timePeriod: {
                                  startTime: formatted,
                                  endTime,
                                },
                              })
                            }}
                            ref={(el) => {
                              timePickerRefs.current[rowIndex * 2] = el
                            }}
                            isFirstTimePicker={true}
                            onTimeSelected={() => handleTimeSelected(rowIndex)}
                          />
                          <span>-</span>
                          <CustomTimePicker
                            key={`${row.id}-closes`}
                            label='Closes at'
                            defaultValue={convertTimePeriodToMoment(endTime)?.format('h:mm A')}
                            onChange={(val) => {
                              const startTime = row.timePeriod?.startTime || ''
                              const formatted = val ? moment(val, 'h:mm A').format('HH:mm') : ''
                              updateHour(row.id, {
                                timePeriod: {
                                  startTime,
                                  endTime: formatted,
                                },
                              })
                            }}
                            ref={(el) => {
                              timePickerRefs.current[rowIndex * 2 + 1] = el
                            }}
                          />
                        </TimePickersWrapper>
                        {startTime && endTime && isEndTimeAfterStartTime(startTime, endTime) && (
                          <MenuAvailabilityErrorText>
                            Error: the end time has to be greater than the start time.
                          </MenuAvailabilityErrorText>
                        )}
                        {(!startTime || !endTime) && (
                          <MenuAvailabilityErrorText>
                            Error: Please fill out all the required fields.
                          </MenuAvailabilityErrorText>
                        )}
                      </>
                    )}
                  </CustomTimeSectionWrapper>
                </RadioGroup>
              </HolidayHoursAvailableTimeContainer>
              <SectionSeparator style={{ marginTop: 16 }} />
            </HolidayHoursAvailabilityRow>
          )
        })}
        {hasErrors && (
          <MenuAvailabilityErrorText style={{ marginBottom: '8px' }}>
            Please fix the errors above before proceeding.
          </MenuAvailabilityErrorText>
        )}
      </AvailabilityList>
      <MenuAvailabilityDialogActions>
        <Button
          onClick={addHour}
          size='small'
          variant='textonly'
          narrow
          startIcon={<PlusIcon />}
          style={{
            background: 'none',
            color: '#1B51A4',
            marginRight: 'auto',
            paddingLeft: 0,
          }}
        >
          New Holiday
        </Button>
        <Button onClick={onCancel} size='small' variant='textonly' narrow>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={hasErrors || saveButtonDisabled} size='small' narrow>
          Save
        </Button>
      </MenuAvailabilityDialogActions>
    </>
  )
}

export default HolidayHoursForm
